import React, { useEffect, useMemo } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { SelectField } from "@culturehq/components";
import { SelectOption, SelectValue, SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

type TextSelectOption = SelectOption & {
  styles?: string[];
  weights?: number[];
}

interface TextInputProps {
  activeElement: ElementState;
  children: React.ReactNode;
  defaultValue: string;
  disabled?: boolean;
  name: string;
  preview: Preview;
  onChange?: (value: null | SelectValue | SelectValueWithCategory) => void;
  options: TextSelectOption[];
  propertyName: string;
  useDefaultValue?: boolean;
  onSubtitleChange?: (propertyName: string, value: string) => Promise<void>;
}

const TextProperty: React.FC<TextInputProps> = ({
  activeElement,
  children,
  disabled = false,
  defaultValue,
  name,
  preview,
  options,
  onChange,
  propertyName,
  useDefaultValue = true,
  onSubtitleChange
}) => {
  const { ready } = preview || { ready: false };
  const isTemplateBuilderProperty = propertyName === "dynamic_element" || propertyName === "relative_to";

  const selectFieldValue = useMemo(() => {
    if (propertyName === "dynamic_element") {
      return activeElement.source.name?.split("@@").find((part: string) => part.startsWith("I=")) ?? defaultValue;
    }
    if (propertyName === "relative_to") {
      return activeElement.source.name?.split("@@").find((part: string) => part.startsWith("RT="))?.split("RT=")?.[1] ?? defaultValue;
    }
    return activeElement.source[propertyName] ?? defaultValue;
  }, [activeElement.source, propertyName, defaultValue]);

  useEffect(
    () => {
      const applyModification = async (value: string) => {
        await preview.applyModifications({
          [`${activeElement.source.id}.${propertyName}`]: value
        });
      };

      const undefinedProperty = activeElement.source[propertyName] === undefined;
      const optionFound = options.find(
        option => option.value === activeElement.source[propertyName]
      );

      if (!isTemplateBuilderProperty && !optionFound && options.length > 0
        && ready && useDefaultValue && undefinedProperty) {
        applyModification(defaultValue);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeElement.source, options, propertyName, defaultValue,
      ready, useDefaultValue, activeElement]
  );

  const handleOnSelectChange = async (value: null | SelectValue | SelectValueWithCategory) => {
    if (!isTemplateBuilderProperty) {
      if (onSubtitleChange) {
        await onSubtitleChange(propertyName, value as string || defaultValue);
      } else if (value) {
        if (propertyName !== "font_family") {
          await preview.applyModifications({
            [`${activeElement.source.id}.${propertyName}`]: value || defaultValue
          });
        } else {
          const valueToUse = value || defaultValue;
          const optionFound = options.find(option => option.value === valueToUse);
          await preview.applyModifications({
            [`${activeElement.source.id}.${propertyName}`]: valueToUse,
            [`${activeElement.source.id}.font_weight`]: optionFound?.weights?.find(font => font === 400) ? "400" : optionFound?.weights?.[0],
            [`${activeElement.source.id}.font_style`]: optionFound?.styles?.find(style => style === "normal") ? "normal" : optionFound?.styles?.[0]
          });
        }
      }
    }

    if (onChange) onChange(value);
  };

  return (
    <SelectField
      className="chq-ffd-border"
      disabled={disabled}
      name={name}
      onChange={handleOnSelectChange}
      options={options}
      value={selectFieldValue}
    >
      {children}
    </SelectField>
  );
};

export default TextProperty;
